import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { breakpoints } from 'styles';
import { editorSocket } from 'components/editor/socket';
import { EditorChapters } from 'components/editor/chapters';
import { EditorContent } from 'components/editor/content';
import {
  EditorVersionHistoryTable,
  EditorVersionPreviewInfo
} from 'components/editor/versionHistory';
import { useEditorContext, useEditorDispatch } from '.';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';

const EditorInner = () => {
  const editorState = useEditorContext();

  const editorDispatch = useEditorDispatch();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  // this used to be a ref, but in this component it seems to cause
  // a production issue where a useEffect is not called preventing
  // the editor socket from connecting
  const [isMounted, setIsMounted] = useState(false);

  const [previewTransferredBlocksWarning, setPreviewTransferredBlocksWarning] =
    useState(false);

  // fetch language version translations progress if applicable
  const {
    data: translationProgressData,
    updateData: updateTranslationProgressData
  } = useApiData<GetCourseTranslationProgressRes>({
    onlyIf: Boolean(Object.keys(course.languageVersions)?.length),
    url: `/admin/courses/${course.id}/translation-progress`
  });

  // manage the web socket connection
  useEffect(() => {
    if (
      !isMounted ||
      editorState.editorIsConnecting === undefined ||
      editorState.editorConnectionError === undefined
    ) {
      return;
    }

    if (
      !editorState.editorConnectionError &&
      !editorState.editorIsConnecting &&
      !editorState.editorPreviewVersion
    ) {
      if (editorState.editorPreviewType) {
        editorDispatch({
          type: 'updateContext',
          update: { editorPreviewType: null }
        });
      }
      editorSocket.fetchInitialData({
        chapterId: editorState.editorCurrentChapter.id
      });
    }
  }, [editorState.editorIsConnecting, editorState.editorConnectionError]);

  // if preview mode is active for the initial render, then fetch preview data
  useEffect(() => {
    if (isMounted && editorState.editorPreviewVersion) {
      editorDispatch({
        type: 'updateContext',
        update: { editorIsFetching: true }
      });
      if (editorSocket) editorSocket.disconnect();
      fetchPreviewData();
    }
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted) return;

    if (editorSocket) editorSocket.disconnect();

    if (!editorState.editorPreviewVersion) {
      editorSocket.connect({
        chapterId: editorState.editorCurrentChapter.id,
        dispatch: editorDispatch
      });
    }
  }, [
    editorState.editorCurrentChapter?.id,
    editorState.editorPreviewVersion,
    isMounted
  ]);

  useEffect(() => {
    if (!isMounted || editorState.editorIsDraft === undefined) return;

    if (
      editorState.editorIsDraft !== editorState.editorCurrentChapter.isDraft
    ) {
      editorDispatch({
        type: 'updateContext',
        update: {
          editorChapters: [
            ...editorState.editorChapters.slice(
              0,
              editorState.editorCurrentChapter.number - 1
            ),
            {
              ...editorState.editorCurrentChapter,
              isDraft: editorState.editorIsDraft
            },
            ...editorState.editorChapters.slice(
              editorState.editorCurrentChapter.number
            )
          ],
          editorCurrentChapter: {
            ...editorState.editorCurrentChapter,
            isDraft: editorState.editorIsDraft
          }
        }
      });
    }
  }, [editorState.editorIsDraft]);

  useEffect(() => {
    if (!isMounted || editorState.editorPublishDate === undefined) return;

    if (
      Date.parse(editorState.editorPublishDate) !==
      Date.parse(editorState.editorCurrentChapter.publishDate as string)
    ) {
      editorDispatch({
        type: 'updateContext',
        update: {
          editorChapters: [
            ...editorState.editorChapters.slice(
              0,
              editorState.editorCurrentChapter.number - 1
            ),
            {
              ...editorState.editorCurrentChapter,
              publishDate: editorState.editorPublishDate
            },
            ...editorState.editorChapters.slice(
              editorState.editorCurrentChapter.number
            )
          ],
          editorCurrentChapter: {
            ...editorState.editorCurrentChapter,
            publishDate: editorState.editorPublishDate
          }
        }
      });
    }
  }, [editorState.editorPublishDate]);

  // re-fetch data when chapter, language version or preview version changes
  useEffect(() => {
    if (!isMounted) return;

    editorDispatch({
      type: 'updateContext',
      update: { editorIsFetching: true }
    });

    if (editorState.editorPreviewVersion) {
      if (editorSocket) editorSocket.disconnect();
      fetchPreviewData();
    } else {
      editorSocket.fetchInitialData({
        chapterId: editorState.editorCurrentChapter.id
      });
    }
  }, [
    editorState.editorActiveLanguageVersion,
    editorState.editorCurrentChapter?.slug,
    editorState.editorPreviewVersion
  ]);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      if (editorSocket) editorSocket.disconnect();
    };
  }, []);

  const fetchPreviewData = async () => {
    try {
      const response = await api.get(
        `/admin/courses/${course.id}` +
          `/versions//${editorState.editorPreviewVersion}/chapter-preview-data` +
          `/${editorState.editorCurrentChapter.id}/${editorState.editorPreviewType}`
      );

      setPreviewTransferredBlocksWarning(
        response.data.displayTransferredBlocksWarning
      );

      // update preview data to EditorContext
      editorDispatch({
        type: 'updateContext',
        update: {
          editorBlocks: response.data.blocks,
          editorBlocksOrder: response.data.order,
          editorChapterTitle: response.data.chapterTitle,
          editorChapterTitleIsEdited: false,
          editorChapterTitleLocales: response.data.chapterTitleLocales,
          editorIsFetching: false,
          editorPreviewVersionCourseLanguage: response.data.courseLanguage,
          editorPreviewVersionCourseLanguageVersions:
            response.data.courseLanguageVersions
        }
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err, messages.coursePreviewError);
    }
  };

  // create a stable list of headings for the current focused chapter
  const headingSignature = useMemo(() => {
    return JSON.stringify(
      editorState.editorBlocksOrder
        ?.filter(
          (blockId) =>
            editorState.editorBlocks[blockId].type === 'heading' &&
            editorState.editorBlocks[blockId].data.visibility !== 'hidden'
        )
        .map((blockId) => ({
          id: blockId,
          level: editorState.editorBlocks[blockId].data.headingLevel ?? 'H2',
          text: editorState.editorBlocks[blockId].data.locales[
            editorState.editorActiveLanguageVersion ||
              editorState.editorPreviewLanguage ||
              course.language
          ].translatable.text
        })) ?? []
    );
  }, [
    editorState.editorActiveLanguageVersion,
    editorState.editorBlocks,
    editorState.editorBlocksOrder,
    editorState.editorPreviewLanguage,
    course.language
  ]);

  const headingsForCurrentChapter = useMemo(
    () => JSON.parse(headingSignature),
    [headingSignature]
  );

  return (
    <div className="editor-inner">
      {editorState.editorPreviewType === 'course' ? (
        <EditorVersionPreviewInfo
          currentChapter={editorState.editorCurrentChapter}
          previewType={editorState.editorPreviewType}
          previewVersion={editorState.editorPreviewVersion}
          showTransferBlocksWarning={previewTransferredBlocksWarning}
        />
      ) : null}

      <div className="wrapper">
        {editorState.editorActiveView === 'course-version-history' ? null : (
          <EditorChapters
            activeLanguageVersion={editorState.editorActiveLanguageVersion}
            chapters={editorState.editorChapters}
            currentChapter={editorState.editorCurrentChapter}
            currentHeadings={headingsForCurrentChapter}
            previewType={editorState.editorPreviewType}
            previewVersion={editorState.editorPreviewVersion}
            translationProgressData={translationProgressData}
          />
        )}

        <div className="content">
          {editorState.editorActiveView !== 'edit' ? (
            <EditorVersionHistoryTable
              activeLanguageVersion={editorState.editorActiveLanguageVersion}
              activeView={editorState.editorActiveView}
              currentChapter={editorState.editorCurrentChapter}
              isDraft={editorState.editorIsDraft}
              isPublishing={editorState.editorIsPublishing}
              languageVersions={
                editorState.editorPreviewVersionCourseLanguageVersions
              }
              previewType={editorState.editorPreviewType}
              previewVersionCourseLanguage={
                editorState.editorPreviewVersionCourseLanguage
              }
              previewVersionCourseLanguageVersions={
                editorState.editorPreviewVersionCourseLanguageVersions
              }
              publishDate={editorState.editorPublishDate}
              status={editorState.editorHeaderStatus}
              translationReferenceLanguage={
                editorState.editorTranslationReferenceLanguage
              }
              updatedAt={editorState.editorHeaderUpdatedAt}
            />
          ) : (
            <>
              {editorState.editorPreviewType === 'chapter' ? (
                <EditorVersionPreviewInfo
                  currentChapter={editorState.editorCurrentChapter}
                  previewType={editorState.editorPreviewType}
                  previewVersion={editorState.editorPreviewVersion}
                  showTransferBlocksWarning={previewTransferredBlocksWarning}
                />
              ) : null}

              <EditorContent
                translationProgressData={translationProgressData}
                updateTranslationProgressData={updateTranslationProgressData}
              />
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .content {
          flex-grow: 1;
          flex-shrink: 3;
          min-width: 0;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .wrapper {
            align-items: flex-start;
            display: flex;
          }
        }
      `}</style>
    </div>
  );
};

export default EditorInner;
